import React, { useEffect, useRef } from 'react';
import { CheckIcon, LockIcon, ShieldCheckIcon } from 'lucide-react';
import { bonuses, offer, price } from '../data/content';
import { trackPixel } from '../utils/metaPixel';
import { CtaButton } from './CtaButton';
import { ImageSlot } from './ImageSlot';
import { SectionHeading } from './SectionHeading';
type OfferSectionProps = {
  offerMockupUrl: string;
};

/** One row of the value stack: label on the left, value on the right (same values as the checkout) */
function StackRow({
  label,
  value,
  strong,
  children
}: {
  label: string;
  value: string;
  strong?: boolean;
  children?: React.ReactNode;
}) {
  return <li className="py-2.5" data-id="element-167" data-mp="refxm852656h">
      <div className="flex items-baseline justify-between gap-4" data-id="element-168" data-mp="rc3kq9e5ql0a">
        <span className={`text-[15px] leading-snug ${strong ? 'font-semibold text-ink' : 'text-ink/80'}`} data-id="element-169" data-mp="wchhgvab68j0">{label}</span>
        <span className="shrink-0 font-display text-[16px] font-extrabold tabular-nums text-ink" data-id="element-170" data-mp="2t7dymr8zo42i">{value}</span>
      </div>
      {children}
    </li>;
}
export function OfferSection({
  offerMockupUrl
}: OfferSectionProps) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    let fired = false;
    const observer = new IntersectionObserver(entries => {
      if (!fired && entries.some(e => e.isIntersecting)) {
        fired = true;
        trackPixel('ViewContent');
        observer.disconnect();
      }
    }, {
      threshold: 0.25
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return <section ref={ref} id="offer" aria-labelledby="offer-title" className="scroll-mt-0 bg-brand py-12 md:py-20" data-id="element-171" data-mp="1ckdci6fm2g8t">
      <div className="mx-auto max-w-page px-5 md:px-8" data-id="element-172" data-mp="17mbs9i6g8avr">
        <SectionHeading id="offer-title" tone="dark" eyebrow={offer.eyebrow} title={offer.title} data-id="element-173" data-mp="16sywcj0z8fo" />

        <div className="mx-auto mt-8 max-w-[820px] overflow-hidden rounded-2xl bg-white md:mt-12" data-id="element-174" data-mp="3sckmb45f46ys">
          {/* The mockup has a pure white background, so this area stays white */}
          <div className="aspect-[4/3] bg-white px-2 pt-3 sm:aspect-[16/10] sm:px-6 sm:pt-6" data-id="element-175" data-mp="2ng7k22elacp">
            <ImageSlot url={offerMockupUrl} alt={offer.mockupAlt} slotName="offerMockupUrl" fit="contain" data-id="element-176" data-mp="3ktl431tp0lh0" />
          </div>

          <div className="p-4 pb-6 sm:p-8 md:p-10" data-id="element-177" data-mp="3ou4yhxmvtazg">
            <div className="text-center" data-id="element-178" data-mp="3owr7m1pf7yjs">
              <p className="font-display text-[12px] font-black tracking-[0.14em] text-coral-ink" data-id="element-179" data-mp="3b388mekcb7w3">{offer.kicker}</p>
              <h3 className="mt-1 font-display text-[26px] font-black uppercase leading-tight tracking-tight text-brand md:text-[32px]" data-id="element-180" data-mp="3fv0515q6g7u5">{offer.name}</h3>
              <p className="mx-auto mt-2 max-w-[560px] text-[16px] leading-relaxed text-ink/75" data-id="element-181" data-mp="364exutwftuzm">{offer.description}</p>
            </div>

            <div className="mt-8" data-id="element-182" data-mp="2s5p0re9mbvb9">
              <h4 className="font-display text-[13px] font-black tracking-[0.1em] text-brand" data-id="element-183" data-mp="2s6qmcco9g078">{offer.includedTitle}</h4>
              <ul className="mt-4 grid gap-3 md:grid-cols-2 md:gap-x-8" data-id="element-184" data-mp="2dhxbx8xoznr2">
                {offer.included.map(item => <li key={item} className="flex gap-3 text-[16px] leading-snug text-ink" data-id="element-185" data-mp="28jdr5buu4i0a">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-leaf text-white" data-id="element-186" data-mp="2mj4wko7acq2o">
                      <CheckIcon className="h-3 w-3" strokeWidth={3} aria-hidden="true" data-id="element-187" data-mp="2ig8kzhg5xu88" />
                    </span>
                    {item}
                  </li>)}
              </ul>
            </div>

            {/* Value stack — exactly the checkout values: $159 (the 5 volumes) + $155 in bonuses = $314 → today $27 */}
            <div className="mt-8 rounded-xl bg-sun-soft px-4 py-5 sm:px-8 sm:py-6" data-id="element-188" data-mp="368fd24euvf1u">
              <h4 className="text-center font-display text-[13px] font-black tracking-[0.12em] text-ink/80" data-id="element-189" data-mp="318b36x8gdikl">{offer.stackTitle}</h4>
              <ul className="mt-3 divide-y divide-ink/10" data-id="element-190" data-mp="3a8hfwgwbueip">
                <StackRow label={offer.mainItem.label} value={offer.mainItem.value} strong data-id="element-191" data-mp="3aa0wk8lwarl0">
                  <ul className="mt-2 space-y-1 border-l-2 border-sun pl-3" data-id="element-192" data-mp="18iikz9dczn9t">
                    {offer.volumeLines.map(line => <li key={line} className="text-[13px] leading-snug text-ink/75" data-id="element-193" data-mp="1i8tqh0coy4it">
                        {line}
                      </li>)}
                  </ul>
                </StackRow>
                {bonuses.items.map(b => <StackRow key={b.label} label={`${b.label.replace('BONUS', 'Bonus')} · ${b.short}`} value={b.value} data-id="element-194" data-mp="plqtwdrks3v7" />)}
              </ul>
              <div className="mt-1 flex items-baseline justify-between gap-4 border-t-2 border-ink/20 pt-3" data-id="element-195" data-mp="ykcleyf0yt84">
                <span className="font-display text-[15px] font-black uppercase tracking-wide text-ink" data-id="element-196" data-mp="13k7cp32ppktp">{offer.totalLabel}</span>
                <s className="font-display text-[22px] font-extrabold tabular-nums text-ink/55 decoration-coral decoration-[3px]" data-id="element-197" data-mp="29zhpo6ut0ztu">{offer.totalValue}</s>
              </div>
              <div className="mt-4 text-center" data-id="element-198" data-mp="2p3ap9c3uz7w0">
                <p className="font-display text-[13px] font-black tracking-[0.16em] text-coral-ink" data-id="element-199" data-mp="2p55bctqv961o">{offer.todayLabel}</p>
                <p className="mt-1 font-display text-[4.6rem] font-black leading-none tracking-tight text-brand md:text-[5.6rem]" data-id="element-200" data-mp="2fd7yzjo4g81d">{price}</p>
                <p className="mt-2 text-[14px] font-semibold text-ink/80" data-id="element-201" data-mp="2bal04yt19muo">{offer.priceNote}</p>
                <p className="mt-2 inline-block rounded-md bg-leaf-ink px-2.5 py-1 font-display text-[12px] font-black tracking-[0.06em] text-white" data-id="element-202" data-mp="26dkv8r7x6q55">{offer.saveNote}</p>
              </div>
            </div>

            {/* The ONLY checkout button on the page */}
            <div className="mx-auto mt-6 max-w-[440px]" data-id="element-203" data-mp="1scs2oc16i54o">
              <CtaButton action="checkout" align="center" data-id="element-204" data-mp="1secvnx7uhg0f" />
            </div>
            <div className="mt-4 text-center" data-id="element-205" data-mp="3qrd78ovdb42i">
              <p className="flex items-center justify-center gap-1.5 text-[13px] font-semibold text-ink/75" data-id="element-206" data-mp="3qq214ieg9k88">
                <LockIcon className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden="true" data-id="element-207" data-mp="1k44g9iui92wt" />
                {offer.ctaNote[0]}
              </p>
              <p className="mt-1 text-[13px] text-ink/65" data-id="element-208" data-mp="1ovw4hmq6uwjs">{offer.ctaNote[1]}</p>
            </div>

            {/* v2: the guarantee right under the button, where the decision is made */}
            <div className="mx-auto mt-5 flex max-w-[480px] items-start gap-3 rounded-xl border-2 border-leaf/40 bg-leaf-soft p-4 text-left" data-id="element-209" data-mp="1f49pdfaqcdbr">
              <ShieldCheckIcon className="mt-0.5 h-7 w-7 shrink-0 text-leaf-ink" strokeWidth={2} aria-hidden="true" data-id="element-210" data-mp="22vf57ugo75m2" />
              <div data-id="element-211" data-mp="27rcg3l95zjrg">
                <p className="font-display text-[15px] font-black uppercase tracking-tight text-leaf-ink" data-id="element-212" data-mp="1tugw4xrdk7wm">{offer.guaranteeTitle}</p>
                <p className="mt-1 text-[14px] leading-snug text-ink/80" data-id="element-213" data-mp="1yrpfv2s68jzz">{offer.guaranteeText}</p>
              </div>
            </div>

            <p className="mx-auto mt-5 max-w-[480px] text-center text-[13px] leading-relaxed text-ink/60" data-id="element-214" data-mp="igv67adoy2a1">{offer.format}</p>
          </div>
        </div>
      </div>
    </section>;
}